/**
 * NCB 核心服務 (完全修復版本 v3)
 * 🔧 修復:
 * 1. signOut 方法名稱統一 (logout → signOut)
 * 2. API Key 驗證邏輯修正
 * 3. 強化 Bearer Token 機制
 */

const NCB_INSTANCE = import.meta.env.VITE_NCB_INSTANCE || '55538_midnight_bar';
const NCB_AUTH_API_URL = import.meta.env.VITE_NCB_AUTH_API_URL || 'https://app.nocodebackend.com/api/user-auth';
const NCB_DATA_API_URL = import.meta.env.VITE_NCB_DATA_API_URL || 'https://app.nocodebackend.com/api/data';

const TOKEN_KEY = 'midnight_bar_token';
const USER_KEY = 'midnight_bar_user';

const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Database-Instance': NCB_INSTANCE,
  };
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const getAuthUrl = (path: string): string =>
  `${NCB_AUTH_API_URL}/${path}?Instance=${NCB_INSTANCE}`;

const getDataUrl = (path: string): string =>
  `${NCB_DATA_API_URL}/${path}?Instance=${NCB_INSTANCE}`;

const saveToken = (data: any) => {
  const token = data?.token || data?.session?.token || data?.user?.token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
  if (data?.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  }
};

const getCachedUser = () => {
  try {
    const cached = localStorage.getItem(USER_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const ncbService = {
  async getProviders() {
    try {
      const res = await fetch(getAuthUrl('providers'), {
        headers: getHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      return res.json();
    } catch {
      return { providers: { email: true, google: true } };
    }
  },

  async register(email: string, password: string, name?: string) {
    const res = await fetch(getAuthUrl('sign-up/email'), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password, name: name || '旅人' }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || data.error || '註冊失敗');
    saveToken(data);
    return data;
  },

  async login(email: string, password: string) {
    const res = await fetch(getAuthUrl('sign-in/email'), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || data.error || '登入失敗，請檢查帳號密碼');
    saveToken(data);
    return data;
  },

  // 🔧 修復: 統一方法名稱為 signOut，同時保留 logout 作為別名
  async signOut() {
    try {
      await fetch(getAuthUrl('sign-out'), {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: '{}',
      });
    } catch (e) {
      console.error('SignOut failed', e);
    } finally {
      clearAuth();
    }
  },

  // 🔧 保留 logout 作為向後相容別名
  async logout() {
    return this.signOut();
  },

  async getSession() {
    const cachedUser = getCachedUser();
    const token = localStorage.getItem(TOKEN_KEY);

    if (cachedUser && token) {
      return { user: cachedUser };
    }

    try {
      const res = await fetch(getAuthUrl('get-session'), {
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuth();
        }
        return { user: null };
      }

      const data = await res.json();
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      }
      return data;
    } catch (e) {
      if (cachedUser) {
        return { user: cachedUser };
      }
      return { user: null };
    }
  },

  getGoogleAuthUrl(callbackUrl: string) {
    return `${NCB_AUTH_API_URL}/sign-in/social?provider=google&callbackURL=${encodeURIComponent(callbackUrl)}&Instance=${NCB_INSTANCE}`;
  },

  async dataRead(table: string, query: string = '') {
    const url = getDataUrl(`read/${table}${query ? '?' + query : ''}`);
    const res = await fetch(url, { headers: getHeaders(), credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataCreate(table: string, payload: any) {
    const res = await fetch(getDataUrl(`create/${table}`), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataUpdate(table: string, id: string, payload: any) {
    const res = await fetch(getDataUrl(`update/${table}/${id}`), {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataDelete(table: string, id: string) {
    const res = await fetch(getDataUrl(`delete/${table}/${id}`), {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },
};